import { CLIENT_STATE_KEYS } from '@thoughtspot/client-state-util';
import { copyTextToClipboard } from '@thoughtspot/radiant-react/widgets/_utils';
import { isKPIChartEnhancementsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import { ErrorTypes, getChartDataValidationError, } from './core/chart-data-validation';
import { getChartType, hasPartialYAxisRange } from './core/chart-model-utils';
import { chartProviders, getChartProvider, getLegendLabels, } from './core/chart-type-specification-service';
import { getGridLinesConfig } from './core/chart-ui-utils';
import { computeMetaDataInformation, processDataModel, } from './data/chart-data-processor-service';
import { generateQueryDefinitions, transformData } from './data/data-util';
import { getEnvOptions, initChartLibEnvOptions, isChartLibInitialized, } from './env';
import { initChartLibEnvOptionsHighcharts } from './env-highcharts';
import { BlinkHighchart } from './highcharts/blink-highchart';
import { updateDataLabels } from './highcharts/data-label.util';
import { generateHighchartConfig, setAxisRange, } from './highcharts/highchart-chart-util';
import { getStyleClass, reflow, render, updateClassNameForZoom, } from './renderer';
import { ChartType, } from './types';
import { createLogger } from './utils/logger';
import { measureBlockAsync } from './utils/perf-utils';
export { getStyleClass };
export var ChartEventTypes;
(function (ChartEventTypes) {
    ChartEventTypes["PointClick"] = "PointClick";
    ChartEventTypes["PointDoubleClick"] = "PointDoubleClick";
    ChartEventTypes["ContextClick"] = "ContextClick";
    ChartEventTypes["MouseOver"] = "MouseOver";
    ChartEventTypes["MouseOut"] = "MouseOut";
    ChartEventTypes["LegendClick"] = "LegendClick";
    ChartEventTypes["LegendColorChange"] = "LegendColorChange";
    ChartEventTypes["AxisClick"] = "AxisClick";
    ChartEventTypes["PivotColumnClick"] = "PivotColumnClick";
    ChartEventTypes["ColumnSort"] = "ColumnSort";
    ChartEventTypes["RenderComplete"] = "RenderComplete";
    ChartEventTypes["ToggleGridline"] = "ToggleGridline";
    ChartEventTypes["UpdateClientState"] = "UpdateClientState";
    ChartEventTypes["UpdateAxisConfigAndClientState"] = "UpdateAxisConfigAndClientState";
    ChartEventTypes["UpdateZoomEvent"] = "UpdateZoomEvent";
    ChartEventTypes["ChangeAnalysisClick"] = "ChangeAnalysisClick";
    ChartEventTypes["ExceptionRaised"] = "ExceptionRaised";
    ChartEventTypes["ChartAreaClick"] = "ChartAreaClick";
    ChartEventTypes["VizRenderUpdate"] = "VizRenderUpdate";
})(ChartEventTypes || (ChartEventTypes = {}));
const logger = createLogger('chart-lib-index');
export class Chart {
    constructor(el, chartModel, options, chartOptions = {}) {
        this.toggleDataLabel = (enabled, columnId) => {
            if (this.chart instanceof BlinkHighchart) {
                this.chart.setDatalabelVisibilityInUserOptions(enabled, columnId);
                updateDataLabels(this.chart, this.chartModel, this.dataModel, columnId);
            }
            else if (this.chart.isGeoMap) {
                this.chart.setDataLabelVisibility(enabled);
            }
        };
        this.setStackLabelVisibility = (enabled) => {
            if (_.isFunction(this.chart.setStackLabelVisibility)) {
                this.chart.setStackLabelVisibility(enabled);
            }
        };
        this.toggleAllDataLabels = (enabled) => {
            if (this.chart) {
                _.forEach(this.chart.userOptions.series, serie => {
                    serie.dataLabels = enabled;
                });
            }
        };
        this.el = el;
        this.options = getEnvOptions(options);
        this.eventsMap = new Map();
        this.chartOptions = _.assign({}, chartOptions);
        if (!isChartLibInitialized()) {
            initChartLibEnvOptions(this.options);
            initChartLibEnvOptionsHighcharts();
        }
        this.prevRenderState = _.assign({});
        try {
            this.initChartViz(chartModel);
        }
        catch (e) {
            this.processGenericError(e);
        }
    }
    getDataError() {
        return this._error;
    }
    getEnvOptions() {
        return this.options;
    }
    reinitializeChartViz(chartModel) {
        var _a, _b;
        if (((_a = this.chartModel.vizProp.systemSeriesColors) === null || _a === void 0 ? void 0 : _a.length) !== 0 &&
            ((_b = chartModel.vizProp.systemSeriesColors) === null || _b === void 0 ? void 0 : _b.length) === 0) {
            return;
        }
        try {
            this.initChartViz(chartModel);
        }
        catch (e) {
            logger.error(e);
            this._error = { type: ErrorTypes.FAILED_TO_RENDER_VIZ };
        }
        if (!!this.chart && _.isFunction(this.chart.updateChartModel)) {
            this.chart.updateChartModel(this.chartModel);
        }
    }
    onDestroy() {
        if (!!this.chart && _.isFunction(this.chart.onDestroy)) {
            this.chart.onDestroy();
        }
    }
    initChartViz(chartModel) {
        this.chartModel = _.assign({}, chartModel);
        this.queryDefinitions = generateQueryDefinitions(this.chartModel);
        this.setData(chartModel.data);
        if (this._error) {
            return;
        }
        this.allSeriesIds = getLegendLabels(this.chartModel, this.dataModel).map(legendLabel => legendLabel.id);
    }
    processGenericError(e) {
        logger.error(e);
        const message = e === null || e === void 0 ? void 0 : e.message;
        if (Object.values(ErrorTypes).includes(message)) {
            this._error = { type: message };
        }
        else {
            this._error = { type: ErrorTypes.FAILED_TO_RENDER_VIZ };
        }
    }
    setData(data) {
        if (data) {
            this.data = transformData(data);
            this.processData();
            this._error = this._error || this.processDataError();
        }
    }
    processDataError() {
        const dataValidationError = getChartDataValidationError(this.dataModel, this.chartModel);
        if (dataValidationError) {
            return dataValidationError;
        }
        return undefined;
    }
    hasNoData() {
        if (!this.data) {
            return true;
        }
        const emptyDataArray = this.data.filter(res => {
            return res.data.length === 0;
        });
        if (emptyDataArray.length > 0) {
            return true;
        }
        return false;
    }
    processData() {
        const isKpiChart = this.chartType === ChartType.Kpi;
        const shouldBypassNoDataForKpi = isKpiChart && isKPIChartEnhancementsEnabled();
        if (this.hasNoData() && !shouldBypassNoDataForKpi) {
            this._error = { type: ErrorTypes.NO_DATA };
            return;
        }
        if (this.hasNoData() && shouldBypassNoDataForKpi) {
            return;
        }
        this.dataModel = processDataModel(this.chartModel, this.data, this.queryDefinitions, this.getEnvOptions(), this.chartOptions, () => {
            return this.eventsMap;
        });
        if (this.hasNoData()) {
            this._error = { type: ErrorTypes.NO_DATA };
            return;
        }
        this.metaInfo = computeMetaDataInformation(this.chartModel, this.dataModel);
    }
    getRenderOptions() {
        const { isDataSourceDefault, disableColorRotation, shouldShowHighchartTitle, isPrintMode, hideAxisIcon, } = this.chartOptions;
        const enableGridline = this.options.flags.isGridlinesEnabledByDefault;
        const { xGridlineEnabled, yGridlineEnabled } = getGridLinesConfig(this.chartModel, enableGridline);
        return {
            xGridlineEnabled,
            yGridlineEnabled,
            disableColorRotation,
            isDataSourceDefault,
            shouldShowHighchartTitle,
            enablePiechartAnimation: !isPrintMode,
            hideAxisIcon,
        };
    }
    enableXAxisGridLines(enabled) {
        this.chart.enableXAxisGridLines(enabled);
    }
    enableYAxisGridLines(enabled) {
        this.chart.enableYAxisGridLines(enabled);
    }
    getDataModel() {
        return this.dataModel;
    }
    get chartType() {
        return getChartType(this.chartModel);
    }
    on(eventType, handler) {
        if (this.eventsMap.has(eventType)) {
            logger.warn(`An event handler for ${eventType} exists and will be overridden`);
        }
        this.eventsMap.set(eventType, handler);
        return this;
    }
    updateSeriesVisibility(visibleSeriesNames) {
        var _a;
        _.set(this.chartModel, [
            'vizProp',
            CLIENT_STATE_KEYS.chartProperties,
            CLIENT_STATE_KEYS.visibleSeriesNames,
        ], [...visibleSeriesNames]);
        if (this.chart instanceof BlinkHighchart) {
            updateDataLabels(this.chart, this.chartModel, this.dataModel);
        }
        try {
            this.chart.setVisibleSeriesVisibility(new Set([...visibleSeriesNames]));
            this.redraw();
            if (this.chart instanceof BlinkHighchart &&
                hasPartialYAxisRange(this.chartModel)) {
                setAxisRange(this.chart, this.chartModel);
                this.redraw();
            }
            const xAxis = (_a = this.chart.axes) === null || _a === void 0 ? void 0 : _a[0];
            if (xAxis && xAxis.dataMin > xAxis.tickPositions[0]) {
                this.reflow();
            }
        }
        catch (e) {
            logger.error('unable to update Series Visibility', e);
            this.processGenericError(e);
        }
    }
    updateChartColor(props, val, renderOnUpdate = true) {
        _.set(this.chartModel, ['vizProp', ...props], val);
        if (renderOnUpdate) {
            this.render();
        }
    }
    getVizModel() {
        return this.chartModel;
    }
    getSelectedPoints() {
        return this.chart.getSelectedPoints();
    }
    getChartContainer() {
        var _a, _b;
        return (_b = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.container) !== null && _b !== void 0 ? _b : null;
    }
    isInPanMode() {
        return (this.chart &&
            _.isFunction(this.chart.isInPanMode) &&
            this.chart.isInPanMode());
    }
    switchChartZoomMode() {
        if (this.el) {
            updateClassNameForZoom(this.el);
        }
        if (this.isInPanMode()) {
            this.chart.switchToZoomMode();
        }
        else {
            this.chart.switchToPanMode();
        }
        this.chart.resetPointer();
    }
    chartZoomOut() {
        this.chart.zoomOut();
    }
    togglePivotSummaries(updatedChartModel) {
        if (this.chart) {
            if (this.chart.togglePivotSummaries) {
                this.chart.togglePivotSummaries(updatedChartModel);
            }
            else {
                logger.warn('chart object is missing the togglePivotSummaries method');
            }
        }
    }
    setMarkerVisibility(markerVisible) {
        this.chart.setMarkerVisibility(markerVisible);
    }
    setAxisRangeForAxes(id, range) {
        const axisIdx = _.findIndex(this.chart.axes, axis => _.get(axis, 'userOptions.id') === id);
        this.chart.setAxisRange(axisIdx, range, false);
    }
    getConfig() {
        const chartProvider = getChartProvider(this.chartType, !!this.chartModel.cortexData);
        const generateConfig = getConfigGeneratorForProvider(chartProvider);
        const config = generateConfig(this.chartModel, this.dataModel, this.el.getBoundingClientRect(), this.eventsMap, this.getRenderOptions(), this.options);
        return config._highchartConfig ? config._highchartConfig : config;
    }
    redraw() {
        this.chart.redraw();
    }
    hasCustomDownload() {
        var _a;
        return !!((_a = this.chart) === null || _a === void 0 ? void 0 : _a.hasCustomDownload);
    }
    hasInternalChartError() {
        var _a, _b;
        return ((_a = this.chart) === null || _a === void 0 ? void 0 : _a.hasInternalChartError)
            ? (_b = this.chart) === null || _b === void 0 ? void 0 : _b.hasInternalChartError()
            : false;
    }
    downloadChart(name) {
        if (_.isFunction(this.chart.downloadChart)) {
            this.chart.downloadChart(name);
        }
    }
    setThemeMode(mode) {
        if (_.isFunction(this.chart.setThemeMode)) {
            this.chart.setThemeMode(mode);
        }
    }
    async render() {
        await measureBlockAsync('ChartPrimaryAndSecondaryRender', async () => {
            await this.primaryRender();
            await this.secondaryRender();
        });
        return this;
    }
    async secondaryRender() {
        await measureBlockAsync('secondaryRender', async () => {
            if (!this.chart) {
                logger.warn('chart is not rendered, chart object is missing');
                return;
            }
            try {
                await this.chart.secondaryRender(this.chartModel, this.dataModel);
            }
            catch (e) {
                logger.warn('secondary render failed', e);
                this.processGenericError(e);
            }
        });
    }
    async primaryRender() {
        try {
            await measureBlockAsync('primaryRenderingWithougtReflow', async () => {
                const { chart, prevRenderState } = await render(this, this.el, this.chartType, this.chartModel, this.dataModel, this.eventsMap, this.getRenderOptions(), this.options, this.prevRenderState, this.chartOptions, this.getDataError());
                this.chart = chart;
                this.prevRenderState = prevRenderState;
            });
            await this.reflow();
        }
        catch (err) {
            logger.error('unable to render the chart', err);
            this.processGenericError(err);
            throw new Error(err);
        }
        return this;
    }
    async reflow(chartOptions) {
        await measureBlockAsync('chartReflow', async () => {
            if (this.chart) {
                try {
                    const { prevRenderState } = await reflow(this, this.chart, this.chartModel, this.dataModel, this.el, (this.chartOptions = _.assignWith(this.chartOptions, chartOptions)), this.prevRenderState, this.eventsMap, this.options, this.getRenderOptions());
                    this.prevRenderState = prevRenderState;
                }
                catch (e) {
                    logger.warn('chart reflow was erroneous', e);
                    this.processGenericError(e);
                }
            }
        });
        return this;
    }
    copyToClipboard() {
        var _a, _b;
        let textToBeCopied;
        switch (this.chartType) {
            case ChartType.PivotTable:
                textToBeCopied = (_b = (_a = this.chart) === null || _a === void 0 ? void 0 : _a.clickedCell) === null || _b === void 0 ? void 0 : _b.text;
                break;
            default:
        }
        if (textToBeCopied !== undefined)
            copyTextToClipboard(textToBeCopied);
    }
}
function noOpConfigGenerator() {
    return {};
}
function getConfigGeneratorForProvider(chartProvider) {
    switch (chartProvider) {
        case chartProviders.HIGHCHART:
            return generateHighchartConfig;
        default:
            return noOpConfigGenerator;
    }
}
//# sourceMappingURL=index.js.map